/**
 * API Client
 * HTTP client for external API integrations
 */

import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { config } from '@/config'

// Create axios instance for CM Collective / Data API
// NOTE: baseURL is NOT set here — it is resolved lazily in the request interceptor
// so that window.__env__ (written by docker-entrypoint.sh at container startup)
// is guaranteed to be available before the first request is made.
export const cmApi: AxiosInstance = axios.create({
    timeout: 60000, // Increased timeout for data operations
    headers: {
        'Content-Type': 'application/json',
    },
})

import { ensureTokenFresh } from './supabase'

// Resolve baseURL and auth token at request time, not at module load time
cmApi.interceptors.request.use(async (requestConfig) => {
    // Set baseURL dynamically so runtime window.__env__ is always used
    requestConfig.baseURL = config.dataApi.baseUrl

    // Attach fresh Supabase JWT if user is authenticated
    const supabaseToken = await ensureTokenFresh()
    if (supabaseToken) {
        requestConfig.headers['Authorization'] = `Bearer ${supabaseToken}`
    }

    return requestConfig
})

// Error handler — logs detailed status, error codes, and server messages to DevTools console
cmApi.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        const status = error.response?.status ?? 'NETWORK_ERROR'
        const data: any = error.response?.data
        const code = data?.code || (status === 404 && typeof data === 'string' && data.includes('Cannot') ? 'ROUTE_NOT_FOUND' : 'API_ERROR')
        const message = data?.error || data?.message || (typeof data === 'string' ? data : error.message)
        const path = error.config?.url || 'unknown path'

        console.error(`[CM API Error] ${status} (${code}) on ${path}: ${message}`)
        return Promise.reject(error)
    }
)

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
    success: boolean
    data?: T
    error?: string
}

/**
 * Wrap API calls with error handling
 */
export async function apiCall<T>(
    fn: () => Promise<T>
): Promise<ApiResponse<T>> {
    try {
        const data = await fn()
        return { success: true, data }
    } catch (error) {
        const message = error instanceof Error ? error.message : 'An error occurred'
        return { success: false, error: message }
    }
}

export default cmApi
